import React, { useRef, useState } from 'react';
import { LuArrowLeft, LuFileText, LuPen, LuPin } from 'react-icons/lu';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { SidebarToggleButton } from '@/components/SidebarToggleButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Page } from '@/components/ui/page';
import { UniversalSearchButton } from '@/components/UniversalSearchButton';
import { useAiChatSidebarState } from '@/views/ai-chat/ai-chat-state';
import { useDockState } from '@/views/dock/dock-state';
import { useHouseholdTeamNameResolver } from 'views/admin/tenantTeam/tenant-team-queries';
import { getNotePersonName } from 'views/household/HouseholdNotes/note-utils';
import { PageSpinner, Toast } from 'widgets';

import { HouseholdNoteForm, HouseholdNoteFormRef } from './HouseholdNoteForm';
import { useMeetingTranscript, useNoteQuery, useUpdateNoteMutation } from './note-queries';
import { NoteBodyCard } from './NoteBodyCard';
import { NoteTranscriptTabs } from './NoteTranscriptTabs';

export const MeetingNotePage = () => {
    const [searchParams] = useSearchParams();
    const noteGUID = searchParams.get('noteGUID') ?? '';
    const navigate = useNavigate();
    const { isOpen: isSidebarOpen, toggle: toggleSidebar } = useAiChatSidebarState();
    const { addUpdateNoteDockItem } = useDockState();
    const [isEditing, setIsEditing] = useState(false);
    const formRef = useRef<HouseholdNoteFormRef>(null);

    const noteQuery = useNoteQuery(noteGUID);
    const updateNoteMutation = useUpdateNoteMutation('', noteGUID);
    const { nameFor } = useHouseholdTeamNameResolver(noteQuery.data?.householdGUID);
    const transcript = useMeetingTranscript(noteQuery.data?.meetingBotGUID);

    if (noteQuery.isLoading) {
        return <PageSpinner />;
    }

    if (!noteQuery.data) {
        return null;
    }

    const note = noteQuery.data;
    const personName = getNotePersonName(note.createdBy, nameFor);
    const updatedPersonName = note.updatedBy ? getNotePersonName(note.updatedBy, nameFor) : personName;

    const handlePin = () => {
        const formValues = formRef.current?.formMethods.getValues();
        addUpdateNoteDockItem(noteGUID, note.householdGUID, formValues);
    };

    return (
        <Page>
            <div className="flex h-full flex-col overflow-hidden">
                {/* Header */}
                <div className="border-border flex flex-shrink-0 items-center gap-3 border-b px-4 py-3">
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-1)}
                        className="text-muted-foreground hover:text-foreground flex items-center gap-1.5 px-2"
                    >
                        <LuArrowLeft className="h-4 w-4" />
                        <span>Back</span>
                    </Button>

                    <div className="border-border h-5 w-px border-l" />

                    <LuFileText className="text-muted-foreground h-5 w-5 flex-shrink-0" />
                    <h1 className="flex-1 truncate text-lg font-semibold">{note.subject || 'Untitled Note'}</h1>

                    {!isEditing && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setIsEditing(true)}
                            className="flex items-center gap-1.5"
                        >
                            <LuPen className="h-4 w-4" />
                            <span>Edit Note</span>
                        </Button>
                    )}

                    <UniversalSearchButton />

                    <SidebarToggleButton
                        isOpen={isSidebarOpen}
                        onToggle={() => toggleSidebar()}
                        openLabel="Open sidebar"
                        closeLabel="Close sidebar"
                    />
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto p-6">
                    {isEditing ? (
                        <Card className="bg-white">
                            <CardContent className="p-6">
                                <div className="mb-6 flex items-center justify-between">
                                    <h2 className="text-lg font-semibold">Note details</h2>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handlePin}
                                        className="flex items-center gap-1.5"
                                    >
                                        <LuPin className="h-4 w-4" />
                                        <span>Pin</span>
                                    </Button>
                                </div>
                                <HouseholdNoteForm
                                    ref={formRef}
                                    showHouseholdInput={false}
                                    defaultValues={{
                                        subject: note.subject ?? '',
                                        content: note.content ?? '',
                                    }}
                                    onSubmit={(data) => {
                                        updateNoteMutation
                                            .mutateAsync({ ...data, noteGUID })
                                            .then(() => {
                                                Toast.success('Note updated successfully');
                                                setIsEditing(false);
                                            })
                                            .catch((err) => {
                                                Toast.error(
                                                    `Failed to update note: ${err?.response?.data?.message ?? err?.message ?? 'Unknown error'}`,
                                                    err,
                                                );
                                            });
                                    }}
                                    onCancel={() => setIsEditing(false)}
                                />
                            </CardContent>
                        </Card>
                    ) : (
                        <NoteTranscriptTabs transcript={transcript}>
                            <NoteBodyCard note={note} personName={personName} updatedPersonName={updatedPersonName} />
                        </NoteTranscriptTabs>
                    )}
                </div>
            </div>
        </Page>
    );
};
